import React, { useEffect, useState } from 'react';
import { Button, Row, Col, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { RequiredLabel } from '../../utils/validators';
import { parseMoney, formatMoney } from '../../utils/money';
import { getCategories } from '../../services/Configs/ItemCategoriesService';
import { getLocations } from '../../services/Configs/LocationsService';
import { getStatuses } from '../../services/Other/GeneralQueries';
import { serviceAddItem } from '../../services/Items/AddItem';
import { uploadItemFiles } from '../../services/Items/UploadFiles';

const getDate = () => new Date().toISOString().slice(0, 10);
const getUserId = () => sessionStorage.getItem('userId') ?? 0;

const extractId = (res) =>
    res?.id ?? res?.data?.id ?? res?.result?.id ?? res?.item?.id ?? null;

// Tipos MIME permitidos para archivos
const allowedFileTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
];

function AddItemModal({ show, onClose, onSuccess }) {
    const [categories, setCategories] = useState([]);
    const [locations, setLocations] = useState([]);
    const [statuses, setStatuses] = useState([]);

    const [finalPriceText, setFinalPriceText] = useState(formatMoney(0));
    const [finalCostText, setFinalCostText] = useState(formatMoney(0));

    const [selectedFiles, setSelectedFiles] = useState([]);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        locationId: 0,
        categoryId: 0,
        statusId: 0,
        unitOfWeightMeasurement: '',
        acquisitionDate: getDate(),
        userId: getUserId(),
        final_price: 0,
    });

    useEffect(() => {
        if (!show) return;

        (async () => {
            const [cats, locs, stats] = await Promise.all([
                getCategories(),
                getLocations(),
                getStatuses(),
            ]);

            const normalizedCats = Array.isArray(cats) ? cats : [];
            const allowedCategories = normalizedCats.filter(
                (c) => !String(c.name || '').toLowerCase().includes('merma')
            );

            const normalizedLocations = Array.isArray(locs) ? locs : [];
            const normalizedStatuses = Array.isArray(stats) ? stats : [];

            const availableStatus = normalizedStatuses.find(
                (s) => String(s.name || '').toLowerCase() === 'disponible'
            );

            setCategories(allowedCategories);
            setLocations(normalizedLocations);
            setStatuses(normalizedStatuses);

            setFormData((prev) => ({
                ...prev,
                statusId: availableStatus?.id ?? 0,
            }));
        })();
    }, [show]);

    // const handleFileChange = (e) => {
    //     const files = Array.from(e.target.files);

    //     // Validar tipos de archivo
    //     const invalidFiles = files.filter(file => !allowedFileTypes.includes(file.type));

    //     if (invalidFiles.length > 0) {
    //         toast.error('Algunos archivos tienen formatos no permitidos');
    //         return;
    //     }

    //     setSelectedFiles(files);
    // };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const numFields = ['categoryId', 'locationId', 'statusId', 'quantity', 'unitPrice'];
        setFormData((prev) => ({
            ...prev,
            [name]: value === '' ? null : (numFields.includes(name) ? Number(value) : value),
        }));
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            quantity: 1,
            unitPrice: 0,
            locationId: 0,
            categoryId: 0,
            statusId: 1,
            unitOfWeightMeasurement: '',
            acquisitionDate: getDate(),
            userId: getUserId(),
            final_price: 0,
        });
        setFinalPriceText(formatMoney(0));
        setFinalCostText(formatMoney(0));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const pending = [];
        if (!formData.name) pending.push('Nombre');
        if (!formData.categoryId) pending.push('Categoría');
        if (!formData.quantity) pending.push('Cantidad de objetos');
        if (!formData.locationId) pending.push('Ubicación');
        if (!formData.unitOfWeightMeasurement) pending.push('Unidad de medida');

        if (pending.length) {
            toast.warning(`Debes completar: ${pending.join(', ')}`);
            return;
        }

        if (!formData.statusId) {
            toast.error('No se encontró el estado inicial "Disponible".');
            return;
        }

        // Si hay archivos, subirlos
        // if (selectedFiles.length > 0) {
        //     uploadItemFiles(addItemResult.id, selectedFiles).then(uploadResult => {
        //         if (uploadResult.result) {
        //             toast.success('Se creó correctamente el objeto con sus archivos');
        //         }
        //         else {
        //             toast.warning('El objeto se creó pero hubo un error al subir los archivos');
        //         }

        //         setTimeout(() => {
        //             navigate('/Inventory');
        //         }, 1500);
        //     }).catch(error => {
        //         toast.warning('El objeto se creó pero hubo un error al subir los archivos');
        //         setTimeout(() => {
        //             navigate('/Inventory');
        //         }, 1500);
        //     });
        // }
        // else {
        //     // No hay archivos, solo mostrar éxito y redirigir
        //     toast.success('Se creó correctamente');
        //     setTimeout(() => {
        //         navigate('/Inventory');
        //     }, 1500);
        // }

        const finalPriceNumber = parseMoney(finalPriceText);
        const finalCostNumber = parseMoney(finalCostText);
        const payload = { ...formData, final_price: finalPriceNumber, unitPrice: finalCostNumber };

        const res = await serviceAddItem(payload);

        toast.success('Se creó correctamente');

        const createdId = extractId(res);
        resetForm();
        onSuccess?.(createdId);
    };

    return (
        <Modal size="lg" show={show} onHide={onClose} backdrop="static" keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>Agregar objeto</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Row className="justify-content-center">
                    <Col xl={10}>
                        <Form onSubmit={handleSubmit}>
                            {/* NOMBRE */}
                            <Row className="mt-2">
                                <Col>
                                    <Form.Label as={RequiredLabel} required>Nombre:</Form.Label>
                                    <Form.Control name="name" value={formData.name} onChange={handleInputChange} />
                                </Col>
                            </Row>

                            {/* DESCRIPCION */}
                            <Row className="mt-3">
                                <Col>
                                    <Form.Label style={{ fontWeight: 'bold' }}>Descripción:</Form.Label>
                                    <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleInputChange} />
                                </Col>
                            </Row>

                            {/* FECHA DE ADQUISICION | CANTIDAD DE OBJETOS */}
                            <Row className="mt-3 g-3">
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Fecha de adquisición:</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="acquisitionDate"
                                        value={formData.acquisitionDate}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Cantidad de objetos:</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </Row>

                            {/* COSTO UNITARIO | PRECIO FINAL */}
                            <Row className="mt-3 g-3">
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Costo unitario $:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        inputMode="decimal"
                                        value={finalCostText}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => setFinalCostText(e.target.value)}
                                        onBlur={() => {
                                            const n = parseMoney(finalCostText);
                                            setFinalCostText(formatMoney(n));
                                            setFormData((p) => ({ ...p, unitPrice: n }));
                                        }}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Precio final $:</Form.Label>
                                    <Form.Control
                                        type="text"
                                        inputMode="decimal"
                                        value={finalPriceText}
                                        onChange={(e) => setFinalPriceText(e.target.value)}
                                        onBlur={() => {
                                            const n = parseMoney(finalPriceText);
                                            setFinalPriceText(formatMoney(n));
                                            setFormData((p) => ({ ...p, final_price: n }));
                                        }}
                                    />
                                </Col>
                            </Row>

                            {/* CATEGORIA | UBICACION */}
                            <Row className="mt-3 g-3">
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Categoría:</Form.Label>
                                    <Form.Select name="categoryId" value={formData.categoryId ?? 0} onChange={handleInputChange}>
                                        <option value={0}>Seleccionar categoría</option>
                                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </Form.Select>
                                </Col>
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Ubicación:</Form.Label>
                                    <Form.Select name="locationId" value={formData.locationId ?? 0} onChange={handleInputChange}>
                                        <option value={0}>Seleccionar ubicación</option>
                                        {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                                    </Form.Select>
                                </Col>
                            </Row>

                            {/* ESTADO FIJO | UNIDAD DE MEDIDA */}
                            <Row className="mt-3 g-3">
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Estado:</Form.Label>
                                    <Form.Control value="Disponible" disabled readOnly />
                                </Col>
                                <Col md={6}>
                                    <Form.Label as={RequiredLabel} required>Unidad de medida:</Form.Label>
                                    <Form.Select
                                        name="unitOfWeightMeasurement"
                                        value={formData.unitOfWeightMeasurement ?? ''}
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Seleccionar unidad</option>
                                        <option value="Kilogramos">Kilogramos</option>
                                        <option value="Litros">Litros</option>
                                        <option value="Botellas">Botellas</option>
                                        <option value="Mililitros">Mililitros</option>
                                        <option value="Cajas">Cajas</option>
                                    </Form.Select>
                                </Col>
                            </Row>

                            {/* SUBIR ARCHIVOS */}
                            {/* <Row className="mt-3 g-3">
                                <Col>
                                    <Form.Group controlId="formFiles">
                                        <Form.Label>Adjuntar archivos (opcional):</Form.Label>
                                        <Form.Control
                                            type="file"
                                            multiple
                                            accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.xls,.xlsx,.doc,.docx"
                                            onChange={handleFileChange}
                                        />
                                        <Form.Text className="text-muted">
                                            Formatos permitidos: Imágenes (JPG, PNG, GIF, WEBP), PDF, Excel (XLS, XLSX), Word (DOC, DOCX)
                                        </Form.Text>
                                    </Form.Group>
                                    {selectedFiles.length > 0 && (
                                        <div className="mt-2">
                                            <strong>Archivos seleccionados:</strong>
                                            <ul>
                                                {selectedFiles.map((file, index) => (
                                                    <li key={index}>{file.name} ({(file.size / 1024).toFixed(2)} KB)</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </Col>
                            </Row> */}

                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
                                <Button variant="primary" type="submit">Guardar</Button>
                            </div>
                        </Form>
                    </Col>
                </Row>
            </Modal.Body>
        </Modal>
    );
}

export default AddItemModal;